
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'general', name: 'General' },
    { id: 'sourcing', name: 'Cocoa Sourcing' },
    { id: 'export', name: 'Export Process' },
    { id: 'quality', name: 'Quality & Certification' },
    { id: 'sustainability', name: 'Sustainability' }
  ];

  const faqs: FAQItem[] = [
    {
      id: 1,
      question: "What makes Olatunde International Limited different from other cocoa exporters?",
      answer: "We combine 30 years of industry experience with cutting-edge technology and sustainable practices. Our direct relationships with farmers, comprehensive traceability systems, and commitment to ethical sourcing set us apart. We're not just exporters; we're partners in building a sustainable cocoa value chain.",
      category: "general"
    },
    {
      id: 2,
      question: "How do you ensure the quality of your cocoa beans?",
      answer: "Our quality assurance process includes multiple checkpoints: field-level quality training for farmers, collection center inspections, laboratory testing for moisture content and bean quality, and final pre-shipment inspections. We maintain certifications from international bodies and conduct regular third-party audits.",
      category: "quality"
    },
    {
      id: 3,
      question: "What certifications do your cocoa beans carry?",
      answer: "Our cocoa beans are certified under multiple international standards including Rainforest Alliance, UTZ, Fairtrade, and Organic certifications. We also maintain ISO 22000 food safety certification and HACCP compliance for our processing facilities.",
      category: "quality"
    },
    {
      id: 4,
      question: "How do you support cocoa farmers in your supply chain?",
      answer: "We provide comprehensive farmer support including: premium pricing above market rates, agricultural training and best practices workshops, access to quality inputs and farming tools, healthcare and education support for farming communities, and direct payment systems that eliminate intermediaries.",
      category: "sourcing"
    },
    {
      id: 5,
      question: "What is your minimum order quantity for international buyers?",
      answer: "Our minimum order quantity is typically one 20-foot container (approximately 17-18 metric tons). However, we can accommodate smaller orders for new customers or specific quality requirements. We also offer sample quantities for quality evaluation.",
      category: "export"
    },
    {
      id: 6,
      question: "How long does the export process take from order to delivery?",
      answer: "The typical export timeline is 4-6 weeks from order confirmation to port of destination. This includes: 1-2 weeks for sourcing and quality preparation, 1 week for documentation and customs clearance, and 2-3 weeks for ocean freight depending on destination.",
      category: "export"
    },
    {
      id: 7,
      question: "Do you provide traceability information for your cocoa?",
      answer: "Yes, we provide comprehensive traceability data including: farm-level origin information, farmer identification and certification status, harvest date and processing details, quality test results, and chain of custody documentation. This supports our customers' sustainability reporting requirements.",
      category: "sustainability"
    },
    {
      id: 8,
      question: "What payment terms do you offer?",
      answer: "We offer flexible payment terms including: Letter of Credit (LC) at sight or usance, Documents Against Payment (DAP), advance payment with discounts for long-term partners, and structured payment plans for large volume contracts. Terms are negotiated based on order size and customer relationship.",
      category: "export"
    },
    {
      id: 9,
      question: "How do you contribute to sustainable cocoa farming?",
      answer: "Our sustainability initiatives include: climate-smart agriculture training, agroforestry programs to increase farm biodiversity, women's empowerment programs in cocoa communities, child protection and education initiatives, and carbon footprint reduction projects. We're committed to the UN Sustainable Development Goals.",
      category: "sustainability"
    },
    {
      id: 10,
      question: "Can you handle special processing requirements?",
      answer: "Yes, we offer various processing options including: fermentation to specific flavor profiles, different drying methods (sun-dried, mechanical), custom moisture content levels, specific bean size grading, and organic processing in dedicated facilities. We work closely with customers to meet their exact specifications.",
      category: "quality"
    },
    {
      id: 11,
      question: "What regions in Nigeria do you source cocoa from?",
      answer: "We source premium cocoa from multiple regions across Nigeria's cocoa belt including: Ondo State (our primary base), Osun State, Oyo State, Ekiti State, Cross River State, and Edo State. Each region contributes unique flavor characteristics to our diverse cocoa portfolio.",
      category: "sourcing"
    },
    {
      id: 12,
      question: "Do you offer warehousing and logistics services?",
      answer: "Yes, we operate bonded warehouse facilities with: climate-controlled storage environments, pest management systems, inventory management and tracking, customs bonded storage capabilities, and integrated logistics coordination. We can handle storage from harvest to shipment.",
      category: "export"
    }
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-800 to-green-900 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Find answers to common questions about our cocoa sourcing, export processes, 
            quality standards, and sustainability initiatives.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Category Filter */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-amber-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-amber-50 border border-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Items */}
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {filteredFAQs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-${openFAQ === faq.id ? 'subtract' : 'add'}-line text-amber-600 text-xl`}></i>
                    </div>
                  </button>
                  
                  {openFAQ === faq.id && (
                    <div className="px-6 pb-5">
                      <div className="border-t border-gray-100 pt-4">
                        <p className="text-gray-700 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact CTA */}
          <div className="mt-16 text-center">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i className="ri-question-answer-line text-4xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Still Have Questions?
              </h3>
              <p className="text-gray-600 mb-6">
                Can't find the answer you're looking for? Our team is here to help you with any specific questions about our cocoa sourcing and export services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:frontdesk@olatundeintl.com"
                  className="whitespace-nowrap cursor-pointer inline-flex items-center justify-center px-6 py-3 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-mail-line"></i>
                  </div>
                  Email Us
                </a>
                <a
                  href="tel:+2347055689282"
                  className="whitespace-nowrap cursor-pointer inline-flex items-center justify-center px-6 py-3 border-2 border-amber-600 text-amber-600 font-medium rounded-lg hover:bg-amber-600 hover:text-white transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  Call Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
